'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { parseISO, isWithinInterval, subDays, startOfDay, endOfDay } from 'date-fns';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Calendar } from '@/components/ui/calendar';
import {
  Filter,
  Search,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  CalendarIcon,
  X,
} from 'lucide-react';
import {
  FilterableEntity,
  PhishingCampaignEntity,
  TrainingCourseEntity,
  AnnouncementCampaignEntity,
  AdvancedFilterDrawerProps,
  EntityStatus,
  DateRange,
  AdvancedFilters,
  SortField,
  SortOrder,
} from '@/lib/teams-portal/types';
import { useTranslation } from '@/lib/i18n';
import { DATE, formatLearnerDate } from '@/components/user-portal/learner-i18n';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/** A status the API sends in English, in the reader's language; an unknown one as sent. */
function statusLabel(t: Translate, status: string): string {
  switch (status) {
    case 'Completed':
      return t('learner.team.filter.statuses.completed');
    case 'Ongoing':
      return t('learner.team.filter.statuses.ongoing');
    case 'Scheduled':
      return t('learner.team.filter.statuses.scheduled');
    case 'Draft':
      return t('learner.team.filter.statuses.draft');
    default:
      return status;
  }
}

// Status Filter Component
interface StatusFilterProps {
  value: EntityStatus[];
  onChange: (value: EntityStatus[]) => void;
}

function StatusFilter({ value, onChange }: StatusFilterProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const allStatuses: EntityStatus[] = ['Completed', 'Ongoing', 'Scheduled', 'Draft'];
  const isAllSelected = value.length === allStatuses.length;
  const displayText = isAllSelected
    ? t('learner.team.filter.allStatuses')
    : t('learner.team.filter.selectedCount', { count: value.length });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Filter className="h-4 w-4" />
          {t('learner.team.filter.statusButton', { value: displayText })}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">{t('learner.team.filter.status')}</h4>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => onChange(allStatuses)}
              >
                {t('learner.team.filter.all')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => onChange([])}
              >
                {t('learner.team.filter.none')}
              </Button>
            </div>
          </div>
          <Separator />
          <div className="space-y-2">
            {allStatuses.map((status) => (
              <div key={status} className="flex items-center space-x-2">
                <Checkbox
                  id={status}
                  checked={value.includes(status)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      onChange([...value, status]);
                    } else {
                      onChange(value.filter((s) => s !== status));
                    }
                  }}
                />
                <label
                  htmlFor={status}
                  className="text-sm cursor-pointer flex-1"
                >
                  {statusLabel(t, status)}
                </label>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Date Range Picker Component
interface DateRangePickerProps {
  label: string;
  value: DateRange | null;
  onChange: (value: DateRange | null) => void;
}

function DateRangePicker({ label, value, onChange }: DateRangePickerProps) {
  const { t, locale } = useTranslation();
  const [open, setOpen] = useState(false);

  const presets = [
    {
      label: t('learner.team.filter.presets.today'),
      getValue: () => ({
        from: startOfDay(new Date()),
        to: endOfDay(new Date()),
      }),
    },
    {
      label: t('learner.team.filter.presets.last7'),
      getValue: () => ({
        from: subDays(new Date(), 7),
        to: new Date(),
      }),
    },
    {
      label: t('learner.team.filter.presets.last30'),
      getValue: () => ({
        from: subDays(new Date(), 30),
        to: new Date(),
      }),
    },
    {
      label: t('learner.team.filter.presets.last90'),
      getValue: () => ({
        from: subDays(new Date(), 90),
        to: new Date(),
      }),
    },
  ];

  const displayText =
    value?.from && value?.to
      ? t('learner.team.filter.range', {
          from: formatLearnerDate(value.from, locale, { month: 'short', day: 'numeric' }),
          to: formatLearnerDate(value.to, locale, DATE),
        })
      : t('learner.team.filter.allTime');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <CalendarIcon className="h-4 w-4" />
          {t('learner.team.filter.labelled', { label, value: displayText })}
          {value && (
            <X
              className="h-3 w-3 ms-1 hover:bg-muted rounded-sm"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          <div className="border-e p-3 space-y-1">
            {presets.map((preset) => (
              <Button
                key={preset.label}
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  onChange(preset.getValue());
                  setOpen(false);
                }}
              >
                {preset.label}
              </Button>
            ))}
            <Separator className="my-2" />
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              {t('learner.team.filter.clearFilter')}
            </Button>
          </div>
          <div className="p-3">
            <Calendar
              mode="range"
              selected={value || undefined}
              onSelect={(range) => onChange((range as DateRange | undefined) ?? null)}
              numberOfMonths={2}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function AdvancedFilterDrawer({
  open,
  onOpenChange,
  entityType,
  entityLabel,
  entityLabelSingular,
  items,
  selectedIds,
  onApply,
}: AdvancedFilterDrawerProps) {
  const { t, locale } = useTranslation();
  // State management
  const [tempSelectedIds, setTempSelectedIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<AdvancedFilters>({
    statuses: ['Completed', 'Ongoing', 'Scheduled', 'Draft'],
    createdDateRange: null,
    lastUpdatedDateRange: null,
    searchQuery: '',
  });
  const [sortField, setSortField] = useState<SortField>('lastUpdated');
  const [sortOrder, setSortOrder] = useState<SortOrder>('DESC');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });

  // Initialize tempSelectedIds when drawer opens
  useEffect(() => {
    if (open) {
      setTempSelectedIds(new Set(selectedIds));
    }
  }, [open, selectedIds]);

  // Reset pagination when filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [filters, sortField, sortOrder]);

  // Filter and sort items
  const filteredAndSortedItems = useMemo(() => {
    let result = [...items];

    // Filter by status
    if (filters.statuses.length > 0 && filters.statuses.length < 4) {
      result = result.filter(item => filters.statuses.includes(item.status));
    }

    // Filter by created date range
    const createdRange = filters.createdDateRange;
    if (createdRange?.from && createdRange?.to) {
      result = result.filter(item => {
        if (!item.createdAt || typeof item.createdAt !== 'string') return false;
        try {
          const itemDate = parseISO(item.createdAt);
          return isWithinInterval(itemDate, {
            start: startOfDay(createdRange.from!),
            end: endOfDay(createdRange.to!),
          });
        } catch (e) {
          return false;
        }
      });
    }

    // Filter by last updated date range
    const lastUpdatedRange = filters.lastUpdatedDateRange;
    if (lastUpdatedRange?.from && lastUpdatedRange?.to) {
      result = result.filter(item => {
        if (!item.lastUpdated || typeof item.lastUpdated !== 'string') return false;
        try {
          const itemDate = parseISO(item.lastUpdated);
          return isWithinInterval(itemDate, {
            start: startOfDay(lastUpdatedRange.from!),
            end: endOfDay(lastUpdatedRange.to!),
          });
        } catch (e) {
          return false;
        }
      });
    }

    // Filter by search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(item =>
        item.name.toLowerCase().includes(query)
      );
    }

    // Sort
    result.sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      // For date fields, convert to Date objects for comparison
      if (sortField === 'createdAt' || sortField === 'lastUpdated') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (sortField === 'name') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortOrder === 'ASC') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    return result;
  }, [items, filters, sortField, sortOrder]);

  // Paginate items
  const paginatedItems = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredAndSortedItems.slice(start, end);
  }, [filteredAndSortedItems, pagination]);

  const totalPages = Math.ceil(filteredAndSortedItems.length / pagination.pageSize);
  const selectedCount = tempSelectedIds.size;

  // Handlers
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortOrder(field === 'lastUpdated' ? 'DESC' : 'ASC');
    }
  };

  const handleSelectAll = () => {
    const currentPageIds = paginatedItems.map(item => item.id);
    const allSelected = currentPageIds.every(id => tempSelectedIds.has(id));

    const newSet = new Set(tempSelectedIds);
    if (allSelected) {
      currentPageIds.forEach(id => newSet.delete(id));
    } else {
      currentPageIds.forEach(id => newSet.add(id));
    }
    setTempSelectedIds(newSet);
  };

  const handleToggleItem = (id: string) => {
    const newSet = new Set(tempSelectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setTempSelectedIds(newSet);
  };

  const handleClearAll = () => {
    setTempSelectedIds(new Set());
  };

  const handleApply = () => {
    onApply(Array.from(tempSelectedIds));
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  // Status badge color mapping
  const getStatusBadgeVariant = (status: EntityStatus): 'default' | 'secondary' | 'outline' => {
    switch (status) {
      case 'Completed':
        return 'default'; // Will style as green
      case 'Ongoing':
        return 'default'; // Will style as blue
      case 'Scheduled':
        return 'secondary'; // Will style as yellow
      case 'Draft':
        return 'outline'; // Will style as gray
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: EntityStatus): string => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Ongoing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Scheduled':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Draft':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  // Check if all items on current page are selected
  const allCurrentPageSelected = paginatedItems.length > 0 &&
    paginatedItems.every(item => tempSelectedIds.has(item.id));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="end" className="w-full sm:max-w-[60vw] overflow-y-auto p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b">
          <SheetTitle>{t('learner.team.filter.title', { label: entityLabel })}</SheetTitle>
        </SheetHeader>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Filter Controls */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Status Filter */}
            <StatusFilter
              value={filters.statuses}
              onChange={(statuses) => setFilters({ ...filters, statuses })}
            />

            {/* Created Date Range */}
            <DateRangePicker
              label={t('learner.team.filter.created')}
              value={filters.createdDateRange}
              onChange={(range) => setFilters({ ...filters, createdDateRange: range })}
            />

            {/* Last Updated Date Range */}
            <DateRangePicker
              label={t('learner.team.filter.lastUpdated')}
              value={filters.lastUpdatedDateRange}
              onChange={(range) => setFilters({ ...filters, lastUpdatedDateRange: range })}
            />
          </div>

          {/* Search and Sort Controls */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('learner.team.filter.searchByName')}
                value={filters.searchQuery}
                onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                className="ps-9"
              />
            </div>
            <Select
              value={sortField}
              onValueChange={(value) => setSortField(value as SortField)}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={t('learner.team.common.sortBy')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lastUpdated">{t('learner.team.filter.lastUpdated')}</SelectItem>
                <SelectItem value="createdAt">{t('learner.team.filter.createdDate')}</SelectItem>
                <SelectItem value="name">{t('learner.team.filter.name')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Results count */}
          <div className="text-sm text-muted-foreground">
            {t('learner.team.filter.results', { count: filteredAndSortedItems.length })}
          </div>

          {/* Table */}
          <div className="border border-border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allCurrentPageSelected && paginatedItems.length > 0}
                      onCheckedChange={handleSelectAll}
                      aria-label={t('learner.team.filter.selectAllOnPage')}
                    />
                  </TableHead>
                  <TableHead className="cursor-pointer hover:bg-muted" onClick={() => handleSort('name')}>
                    <div className="flex items-center gap-2">
                      {t('learner.team.filter.name')}
                      {sortField === 'name' && (
                        sortOrder === 'ASC' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead>{t('learner.team.filter.status')}</TableHead>
                  <TableHead>{t('learner.team.filter.creator')}</TableHead>
                  <TableHead className="cursor-pointer hover:bg-muted" onClick={() => handleSort('lastUpdated')}>
                    <div className="flex items-center gap-2">
                      {t('learner.team.filter.lastUpdated')}
                      {sortField === 'lastUpdated' && (
                        sortOrder === 'ASC' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer hover:bg-muted" onClick={() => handleSort('createdAt')}>
                    <div className="flex items-center gap-2">
                      {t('learner.team.filter.created')}
                      {sortField === 'createdAt' && (
                        sortOrder === 'ASC' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      )}
                    </div>
                  </TableHead>
                  {/* Entity-specific columns */}
                  {entityType === 'campaign' && (
                    <>
                      <TableHead className="text-end">{t('learner.team.metrics.sent')}</TableHead>
                      <TableHead className="text-end">{t('learner.team.metrics.opened')}</TableHead>
                      <TableHead className="text-end">{t('learner.team.metrics.clicked')}</TableHead>
                    </>
                  )}
                  {entityType === 'training' && (
                    <>
                      <TableHead className="text-end">{t('learner.team.metrics.assigned')}</TableHead>
                      <TableHead className="text-end">{t('learner.team.metrics.completed')}</TableHead>
                    </>
                  )}
                  {entityType === 'announcement' && (
                    <>
                      <TableHead className="text-end">{t('learner.team.metrics.sent')}</TableHead>
                      <TableHead className="text-end">{t('learner.team.metrics.acknowledged')}</TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={entityType === 'campaign' ? 9 : 8} className="text-center py-8 text-muted-foreground">
                      {t('learner.team.filter.noneFound', { label: entityLabel.toLocaleLowerCase(locale) })}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedItems.map((item) => (
                    <TableRow key={item.id} className="hover:bg-muted/50">
                      <TableCell>
                        <Checkbox
                          checked={tempSelectedIds.has(item.id)}
                          onCheckedChange={() => handleToggleItem(item.id)}
                          aria-label={t('learner.team.filter.selectItem', { name: item.name })}
                        />
                      </TableCell>
                      <TableCell className="font-medium text-foreground">{item.name}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(item.status)}>
                          {statusLabel(t, item.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{item.creator}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {(typeof item.lastUpdated === 'string' && formatLearnerDate(item.lastUpdated, locale, DATE)) || t('learner.team.common.notAvailable')}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {(typeof item.createdAt === 'string' && formatLearnerDate(item.createdAt, locale, DATE)) || t('learner.team.common.notAvailable')}
                      </TableCell>
                      {/* Entity-specific columns */}
                      {entityType === 'campaign' && (
                        <>
                          <TableCell className="text-end text-muted-foreground">
                            {(item as PhishingCampaignEntity).sent}
                          </TableCell>
                          <TableCell className="text-end text-muted-foreground">
                            {(item as PhishingCampaignEntity).opened}
                          </TableCell>
                          <TableCell className="text-end text-muted-foreground">
                            {(item as PhishingCampaignEntity).clicked}
                          </TableCell>
                        </>
                      )}
                      {entityType === 'training' && (
                        <>
                          <TableCell className="text-end text-muted-foreground">
                            {(item as TrainingCourseEntity).assigned}
                          </TableCell>
                          <TableCell className="text-end text-muted-foreground">
                            {(item as TrainingCourseEntity).completed}
                          </TableCell>
                        </>
                      )}
                      {entityType === 'announcement' && (
                        <>
                          <TableCell className="text-end text-muted-foreground">
                            {(item as AnnouncementCampaignEntity).sent}
                          </TableCell>
                          <TableCell className="text-end text-muted-foreground">
                            {(item as AnnouncementCampaignEntity).acknowledged}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {filteredAndSortedItems.length > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{t('learner.team.filter.rowsPerPage')}</span>
                <Select
                  value={pagination.pageSize.toString()}
                  onValueChange={(value) =>
                    setPagination({ ...pagination, pageSize: parseInt(value), pageIndex: 0 })
                  }
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">
                  {t('learner.team.filter.pageRange', {
                    from: pagination.pageIndex * pagination.pageSize + 1,
                    to: Math.min((pagination.pageIndex + 1) * pagination.pageSize, filteredAndSortedItems.length),
                    total: filteredAndSortedItems.length,
                  })}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label={t('learner.team.common.previousPage')}
                    onClick={() => setPagination({ ...pagination, pageIndex: pagination.pageIndex - 1 })}
                    disabled={pagination.pageIndex === 0}
                  >
                    <ChevronLeft className="h-4 w-4 rtl-flip" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label={t('learner.team.common.nextPage')}
                    onClick={() => setPagination({ ...pagination, pageIndex: pagination.pageIndex + 1 })}
                    disabled={pagination.pageIndex >= totalPages - 1}
                  >
                    <ChevronRight className="h-4 w-4 rtl-flip" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <SheetFooter className="px-6 py-4 border-t flex-row justify-between items-center">
          <div className="text-sm text-muted-foreground">
            {t('learner.team.filter.itemsSelected', { count: selectedCount })}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleClearAll}>
              {t('learner.team.filter.clearAll')}
            </Button>
            <Button
              className=""
              onClick={handleApply}
            >
              {t('learner.team.filter.apply')}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
